import { Icons } from '../Icons';

export interface EmptyStateProps {
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  illustration?: 'portfolio' | 'search' | 'error' | 'success' | 'rocket' | 'chart' | 'learning';
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: React.ComponentType<{ size?: number }>;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ComponentType<{ size?: number }>;
  };
  tertiaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ComponentType<{ size?: number }>;
  };
  helpLink?: {
    label: string;
    onClick: () => void;
  };
  tips?: string[];
  variant?: 'default' | 'search' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
}

// SVG Illustrations for empty states
const Illustrations = {
  portfolio: () => (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="portfolioGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#EC4899" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="55" fill="url(#portfolioGrad)" />
      <path d="M40 75V50C40 47.2386 42.2386 45 45 45H75C77.7614 45 80 47.2386 80 50V75C80 77.7614 77.7614 80 75 80H45C42.2386 80 40 77.7614 40 75Z" stroke="#8B5CF6" strokeWidth="2.5" fill="none" />
      <path d="M40 55H80" stroke="#8B5CF6" strokeWidth="2" />
      <circle cx="50" cy="67" r="4" fill="#8B5CF6" opacity="0.6" />
      <circle cx="60" cy="67" r="4" fill="#EC4899" opacity="0.6" />
      <circle cx="70" cy="67" r="4" fill="#3B82F6" opacity="0.6" />
      <path d="M45 40L50 45M75 40L70 45" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" />
      {/* Sparkle animation effect */}
      <circle cx="85" cy="35" r="2" fill="#F59E0B">
        <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="30" cy="45" r="1.5" fill="#10B981">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="2.5s" repeatCount="indefinite" />
      </circle>
    </svg>
  ),

  search: () => (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="searchGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="55" fill="url(#searchGrad)" />
      <circle cx="55" cy="55" r="20" stroke="#3B82F6" strokeWidth="3" fill="none" />
      <path d="M70 70L82 82" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" />
      <path d="M48 55H62M55 48V62" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    </svg>
  ),

  error: () => (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="errorGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#EF4444" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#F97316" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="55" fill="url(#errorGrad)" />
      <circle cx="60" cy="60" r="25" stroke="#EF4444" strokeWidth="3" fill="none" />
      <path d="M60 50V62" stroke="#EF4444" strokeWidth="3" strokeLinecap="round" />
      <circle cx="60" cy="70" r="2" fill="#EF4444" />
    </svg>
  ),

  success: () => (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="successGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10B981" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#34D399" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="55" fill="url(#successGrad)" />
      <circle cx="60" cy="60" r="25" stroke="#10B981" strokeWidth="3" fill="none" />
      <path d="M50 60L57 67L72 52" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),

  rocket: () => (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="rocketGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="55" fill="url(#rocketGrad)" />
      <g transform="translate(35, 30)">
        <path d="M25 5C25 5 35 15 35 35C35 45 30 55 25 60C20 55 15 45 15 35C15 15 25 5 25 5Z" stroke="#8B5CF6" strokeWidth="2.5" fill="none" />
        <circle cx="25" cy="30" r="5" stroke="#8B5CF6" strokeWidth="2" fill="none" />
        <path d="M15 45L10 55L20 50" stroke="#EC4899" strokeWidth="2" strokeLinecap="round" />
        <path d="M35 45L40 55L30 50" stroke="#EC4899" strokeWidth="2" strokeLinecap="round" />
        {/* Flame animation */}
        <path d="M22 60L25 70L28 60" fill="#F59E0B">
          <animate attributeName="d" values="M22 60L25 70L28 60;M22 60L25 65L28 60;M22 60L25 70L28 60" dur="0.5s" repeatCount="indefinite" />
        </path>
      </g>
    </svg>
  ),

  chart: () => (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="chartGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10B981" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="55" fill="url(#chartGrad)" />
      <rect x="35" y="70" width="10" height="20" rx="2" fill="#3B82F6" opacity="0.7" />
      <rect x="50" y="55" width="10" height="35" rx="2" fill="#8B5CF6" opacity="0.7" />
      <rect x="65" y="40" width="10" height="50" rx="2" fill="#10B981" opacity="0.7" />
      <rect x="80" y="50" width="10" height="40" rx="2" fill="#F59E0B" opacity="0.7" />
      <path d="M30 75L45 60L60 50L75 35L90 45" stroke="#EC4899" strokeWidth="2" strokeLinecap="round" fill="none" />
      <circle cx="90" cy="45" r="3" fill="#EC4899" />
    </svg>
  ),

  learning: () => (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="learnGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="55" fill="url(#learnGrad)" />
      {/* Book */}
      <path d="M35 40V80L60 70L85 80V40L60 50L35 40Z" stroke="#8B5CF6" strokeWidth="2.5" fill="none" />
      <path d="M60 50V70" stroke="#8B5CF6" strokeWidth="2" />
      {/* Graduation cap */}
      <path d="M60 30L40 38L60 46L80 38L60 30Z" fill="#F59E0B" />
      <path d="M75 40V50" stroke="#F59E0B" strokeWidth="2" />
      <circle cx="75" cy="52" r="2" fill="#F59E0B" />
      {/* Stars */}
      <circle cx="90" cy="35" r="2" fill="#10B981">
        <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="30" cy="30" r="1.5" fill="#EC4899">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="2.5s" repeatCount="indefinite" />
      </circle>
    </svg>
  ),
};

export function EmptyState({
  icon: Icon,
  illustration,
  title,
  description,
  action,
  secondaryAction,
  tertiaryAction,
  helpLink,
  tips,
  variant = 'default',
  size = 'md'
}: EmptyStateProps) {
  // Default icons based on variant
  const defaultIcons = {
    default: Icons.Package,
    search: Icons.Search,
    error: Icons.AlertCircle,
    success: Icons.CheckCircle
  };

  const DisplayIcon = Icon || defaultIcons[variant];
  const IllustrationComponent = illustration ? Illustrations[illustration] : null;

  const sizeClasses = {
    sm: 'empty-state-sm',
    md: 'empty-state-md',
    lg: 'empty-state-lg'
  };

  const variantClasses = {
    default: 'empty-state-default',
    search: 'empty-state-search',
    error: 'empty-state-error',
    success: 'empty-state-success'
  };

  return (
    <div
      className={`empty-state ${sizeClasses[size]} ${variantClasses[variant]}`}
      style={{
        animation: 'fadeIn 0.4s ease-out'
      }}
    >
      {/* Illustration or Icon */}
      {IllustrationComponent ? (
        <div style={{ marginBottom: '16px' }}>
          <IllustrationComponent />
        </div>
      ) : (
        <div
          className="empty-state-icon-wrapper"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05))',
            borderRadius: '50%',
            padding: size === 'sm' ? '16px' : size === 'md' ? '20px' : '24px',
            marginBottom: '16px'
          }}
        >
          <DisplayIcon
            size={size === 'sm' ? 32 : size === 'md' ? 48 : 64}
            className="empty-state-icon"
          />
        </div>
      )}

      <h3
        className="empty-state-title"
        style={{
          fontSize: size === 'sm' ? '16px' : size === 'md' ? '20px' : '24px',
          fontWeight: 600,
          marginBottom: '8px',
          color: 'var(--text)'
        }}
      >
        {title}
      </h3>

      {description && (
        <p
          className="empty-state-description"
          style={{
            fontSize: size === 'sm' ? '13px' : '14px',
            color: 'var(--muted)',
            maxWidth: '400px',
            lineHeight: '1.6',
            marginBottom: '20px'
          }}
        >
          {description}
        </p>
      )}

      {/* Action Buttons */}
      {(action || secondaryAction || tertiaryAction) && (
        <div
          className="empty-state-actions"
          style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: tips ? '0' : '8px'
          }}
        >
          {action && (
            <button
              onClick={action.onClick}
              className="empty-state-action"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, var(--accent), #7C3AED)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.transform = 'translateY(-2px)';
                (e.target as HTMLElement).style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.transform = 'translateY(0)';
                (e.target as HTMLElement).style.boxShadow = '0 4px 14px rgba(139, 92, 246, 0.3)';
              }}
            >
              {action.icon && <action.icon size={16} />}
              {action.label}
            </button>
          )}
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: 'var(--card-bg)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.borderColor = 'var(--accent)';
                (e.target as HTMLElement).style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.borderColor = 'var(--border)';
                (e.target as HTMLElement).style.transform = 'translateY(0)';
              }}
            >
              {secondaryAction.icon && <secondaryAction.icon size={16} />}
              {secondaryAction.label}
            </button>
          )}
          {tertiaryAction && (
            <button
              onClick={tertiaryAction.onClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: 'transparent',
                color: 'var(--muted)',
                border: '1px dashed var(--border)',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.borderColor = 'var(--primary)';
                (e.target as HTMLElement).style.color = 'var(--primary)';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.borderColor = 'var(--border)';
                (e.target as HTMLElement).style.color = 'var(--muted)';
              }}
            >
              {tertiaryAction.icon && <tertiaryAction.icon size={16} />}
              {tertiaryAction.label}
            </button>
          )}
        </div>
      )}

      {/* Tips Section */}
      {tips && tips.length > 0 && (
        <div style={{
          marginTop: '24px',
          padding: '16px 20px',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(139, 92, 246, 0.04))',
          borderRadius: '12px',
          border: '1px solid rgba(99, 102, 241, 0.1)',
          textAlign: 'left',
          maxWidth: '500px'
        }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 600,
            marginBottom: '10px',
            color: 'var(--accent)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <Icons.Zap size={14} />
            Conseils pour bien démarrer
          </div>
          <ul style={{
            fontSize: '13px',
            lineHeight: '1.9',
            margin: 0,
            paddingLeft: '20px',
            color: 'var(--muted)'
          }}>
            {tips.map((tip, index) => (
              <li key={index} style={{ marginBottom: '4px' }}>{tip}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Help Link */}
      {helpLink && (
        <button
          onClick={helpLink.onClick}
          style={{
            marginTop: '16px',
            background: 'transparent',
            border: 'none',
            color: 'var(--accent)',
            fontSize: '13px',
            cursor: 'pointer',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {helpLink.label}
          <Icons.ChevronRight size={14} />
        </button>
      )}
    </div>
  );
}

// Preset variants for common use cases
export const EmptyStatePresets = {
  NoData: (props: Partial<EmptyStateProps>) => (
    <EmptyState
      icon={Icons.Package}
      title="No data yet"
      description="Start by adding your first item"
      {...props}
    />
  ),

  NoResults: (props: Partial<EmptyStateProps>) => (
    <EmptyState
      illustration="search"
      title="Aucun résultat trouvé"
      description="Essayez d'ajuster votre recherche ou vos filtres"
      variant="search"
      {...props}
    />
  ),

  NoPortfolio: (props: Partial<EmptyStateProps> & { onLoadDemo?: () => void }) => (
    <EmptyState
      illustration="portfolio"
      title="Votre portfolio vous attend !"
      description="Commencez par ajouter vos tokens crypto ou essayez avec un portfolio de démonstration."
      action={{
        label: 'Ajouter un token',
        onClick: () => {},
        icon: Icons.Plus
      }}
      secondaryAction={{
        label: 'Importer JSON',
        onClick: () => {},
        icon: Icons.Upload
      }}
      tertiaryAction={props.onLoadDemo ? {
        label: 'Essayer la démo',
        onClick: props.onLoadDemo,
        icon: Icons.Play
      } : undefined}
      tips={[
        'Utilisez ⌘K pour ouvrir la palette de commandes',
        'Plus de 10,000 tokens disponibles via CoinGecko',
        'Vos données restent 100% privées en local'
      ]}
      helpLink={{
        label: 'Voir le guide complet',
        onClick: () => {}
      }}
      {...props}
    />
  ),

  NoTokens: (props: Partial<EmptyStateProps>) => (
    <EmptyState
      illustration="chart"
      title="Ajoutez vos premiers tokens"
      description="Recherchez par nom ou symbole pour commencer à suivre vos positions"
      action={{
        label: 'Ajouter un token',
        onClick: () => {},
        icon: Icons.Plus
      }}
      tips={[
        'Bitcoin = BTC, Ethereum = ETH, etc.',
        'Les prix se mettent à jour toutes les 60s'
      ]}
      {...props}
    />
  ),

  NoPositions: (props: Partial<EmptyStateProps>) => (
    <EmptyState
      icon={Icons.BarChart}
      title="Aucune position enregistrée"
      description="Ajoutez vos achats et ventes pour voir l'analyse de votre portfolio"
      action={{
        label: 'Ajouter une position',
        onClick: () => {},
        icon: Icons.Plus
      }}
      {...props}
    />
  ),

  Error: (props: Partial<EmptyStateProps>) => (
    <EmptyState
      illustration="error"
      title="Oups, une erreur s'est produite"
      description="Nous n'avons pas pu charger ces données. Veuillez réessayer."
      variant="error"
      action={{
        label: 'Réessayer',
        onClick: () => window.location.reload(),
        icon: Icons.RefreshCw
      }}
      {...props}
    />
  ),

  Success: (props: Partial<EmptyStateProps>) => (
    <EmptyState
      illustration="success"
      title="Parfait !"
      description="Tout est à jour"
      variant="success"
      {...props}
    />
  ),

  ComingSoon: (props: Partial<EmptyStateProps>) => (
    <EmptyState
      illustration="rocket"
      title="Bientôt disponible"
      description="Cette fonctionnalité est en cours de développement"
      {...props}
    />
  ),

  Academy: (props: Partial<EmptyStateProps>) => (
    <EmptyState
      illustration="learning"
      title="Prêt à apprendre ?"
      description="Commencez votre parcours d'apprentissage crypto avec nos modules interactifs"
      action={{
        label: 'Commencer le premier cours',
        onClick: () => {},
        icon: Icons.Play
      }}
      tips={[
        'Gagnez des badges en complétant les modules',
        'Contenu adapté à tous les niveaux',
        'Mises à jour régulières avec les dernières tendances'
      ]}
      {...props}
    />
  ),

  Bot: (props: Partial<EmptyStateProps>) => (
    <EmptyState
      illustration="rocket"
      title="Vos bots attendent vos ordres"
      description="Créez votre première stratégie de trading automatisée"
      action={{
        label: 'Créer une stratégie',
        onClick: () => {},
        icon: Icons.Plus
      }}
      secondaryAction={{
        label: 'Voir le tutoriel',
        onClick: () => {},
        icon: Icons.Play
      }}
      tips={[
        'Commencez avec le Paper Trading (simulation)',
        'Testez vos stratégies avec le backtesting',
        'Aucun risque avec les fonds virtuels'
      ]}
      {...props}
    />
  ),
};
